import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import ApiService from "../../../services/categoryManagementApi";

const { reportsAndAnalyticsApi } = ApiService;

// ---- UTC helpers ----
function toUTCStart(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}
function toUTCEnd(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

const OrderReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [search, setSearch] = useState("");
  const [reportGenerated, setReportGenerated] = useState(false);

  // Validation: both dates set and in right order
  const isDateRangeValid =
    fromDate &&
    toDate &&
    !isNaN(new Date(fromDate)) &&
    !isNaN(new Date(toDate)) &&
    toDate >= fromDate;

  const fetchReport = async () => {
    if (!isDateRangeValid) {
      toast.error("Please select a valid date range.");
      return;
    }
    setLoading(true);
    try {
      const response = await reportsAndAnalyticsApi.getUserReport({
        type: "orderReports",
        startDate: toUTCStart(new Date(fromDate)).toISOString(),
        endDate: toUTCEnd(new Date(toDate)).toISOString(),
        search,
      });
      setReportData(response?.data?.data?.result?.orders || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch order report");
      console.error("Error fetching order report:", error);
    } finally {
      setReportGenerated(true);
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setSearch("");
    setReportData([]);
    setReportGenerated(false);
  };

  const exportToExcel = () => {
    const exportData = reportData.map(order => ({
      "Order Number": order.orderNumber,
      "User Name": order.userInfo?.name || "-",
      "User Email": order.userInfo?.email || "-",
      "Dealer Email": order.dealerInfo?.email || "-",
      "Dealer Phone": order.dealerInfo?.phoneNumber || "-",
      "Order Status": order.orderStatus?.stage || "-",
      "Driver Name": order.driverInfo?.name || "-",
      "Driver Name": order.driverInfo?.email || "-",
      "Driver Phone": order.driverInfo?.phoneNumber || "-",
      "Total Amount": order.total || 0,
      "Payment Method": order.paymentMethod || "-",
      "Payment Status": order.paymentStatus || "-",
      "Schedule Date": order.scheduleDate ? new Date(order.scheduleDate).toLocaleDateString() : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Order Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `OrderReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Typography variant="h5" gutterBottom>Order Report</Typography>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={val => {
                setFromDate(val);
                if (toDate && val && toDate < val) setToDate(null);
              }}
              maxDate={toDate || undefined}
              slotProps={{ textField: { fullWidth: true, variant: "outlined" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={val => setToDate(val)}
              minDate={fromDate || undefined}
              maxDate={new Date()}
              slotProps={{ textField: { fullWidth: true, variant: "outlined" } }}
            />
          </Grid>
          {/* <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && fetchReport()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={fetchReport} disabled={loading || !isDateRangeValid}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid> */}
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchReport}
              disabled={loading || !isDateRangeValid}
              sx={{ height: 48 }}
            >
              {loading ? "Loading..." : "Generate Report"}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              sx={{ height: 48 }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {reportData.length > 0 ? (
          <>
            <Button
              variant="contained"
              onClick={exportToExcel}
              sx={{ mb: 2 }}
              disabled={reportData.length === 0}
            >
              Export Excel
            </Button>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order #</TableCell>
                    <TableCell>User Name</TableCell>
                    <TableCell>User Email</TableCell>
                    <TableCell>User Phone</TableCell>
                    <TableCell>Dealer Email</TableCell>
                    <TableCell>Dealer Phone</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Driver Name</TableCell>
                    <TableCell>Driver Email</TableCell>
                    <TableCell>Driver Phone</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell>Payment Status</TableCell>
                    <TableCell>Schedule Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((order, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{order.orderNumber}</TableCell>
                      <TableCell>{order.userInfo?.name || "-"}</TableCell>
                      <TableCell>{order.userInfo?.email || "-"}</TableCell>
                      <TableCell>{order.userInfo?.countryCode + order.userInfo?.phoneNumber || "-"}</TableCell>
                      <TableCell>{order.dealerInfo?.email || "-"}</TableCell>
                      <TableCell>{order.dealerInfo?.phoneNumber || "-"}</TableCell>
                      <TableCell>{order.orderStatus?.stage || "-"}</TableCell>
                      <TableCell>{order.driverInfo?.name || "-"}</TableCell>
                      <TableCell>{order.driverInfo?.email || "-"}</TableCell>
                      <TableCell>{order.driverInfo?.countryCode + order.driverInfo?.phoneNumber || "-"}</TableCell>
                      <TableCell>{Number(order.total).toFixed(2)}</TableCell>
                      <TableCell>{order.paymentMethod || "-"}</TableCell>
                      <TableCell>{order.paymentStatus || "-"}</TableCell>
                      <TableCell>{order.scheduleDate ? new Date(order.scheduleDate).toLocaleDateString() : "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )  : reportGenerated ? (
          <Typography sx={{ mt: 3 }} color="text.secondary" align="center">
            No data found.
          </Typography>
        ) : (
         <Paper
          elevation={2}
          sx={{
            mt: 4,
            width: "100%",
            minHeight: "35vh",           
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fafbfc",
            boxSizing: "border-box",
            p: { xs: 2, sm: 4 },
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Select a date range above and click <strong>Generate Report</strong> to view all orders.
          </Typography>
         
        </Paper>
        
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default OrderReport;
